/**
 * Login / Sign Up Page
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Button, Typography, CircularProgress,
  TextField, Alert, Tabs, Tab,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || (
  window.location.hostname === 'localhost'
    ? 'http://localhost:8000/api'
    : `${window.location.protocol}//${window.location.host}/api`
);

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab]           = useState(0); // 0 = Sign In, 1 = Sign Up
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  const handleTabChange = (_, newVal) => {
    setTab(newVal);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (tab === 0) {
        // Sign In
        await login(email, password);
        navigate('/dashboard');
      } else {
        // Sign Up
        const res = await fetch(`${API_BASE_URL}/auth/signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password, full_name: fullName }),
        });
        const body = await res.json();
        if (!res.ok) {
          const msg = Array.isArray(body.detail)
            ? body.detail.map(e => e.msg).join(', ')
            : body.detail || 'Signup failed';
          throw new Error(msg);
        }
        setSuccess('Account created! You can now sign in.');
        setTab(0);
        setPassword('');
        setFullName('');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #424242 0%, #757575 50%, #D32F2F 100%)',
    }}>
      <Container maxWidth="sm">
        <Paper elevation={24} sx={{ p: 5, borderRadius: 2 }}>
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
              Job Posting Automation
            </Typography>
            <Typography variant="body1" color="text.secondary">
              MAS Holdings — Automated Design Generation
            </Typography>
          </Box>

          <Tabs value={tab} onChange={handleTabChange} centered sx={{ mb: 3 }}>
            <Tab label="Sign In" />
            <Tab label="Sign Up" />
          </Tabs>

          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Box component="form" onSubmit={handleSubmit}>
            {tab === 1 && (
              <TextField
                fullWidth label="Full Name" type="text"
                value={fullName} onChange={(e) => setFullName(e.target.value)}
                required disabled={loading} sx={{ mb: 2 }}
              />
            )}
            <TextField
              fullWidth label="Email address" type="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              required disabled={loading} sx={{ mb: 2 }}
            />
            <TextField
              fullWidth label="Password" type="password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              required disabled={loading}
              helperText={tab === 1 ? 'Min 8 characters, must include uppercase, lowercase and number' : ''}
              sx={{ mb: 3 }}
            />

            <Button
              fullWidth type="submit" variant="contained" size="large"
              disabled={loading}
              sx={{
                py: 1.8, fontSize: '1.1rem', fontWeight: 600,
                background: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #B71C1C 0%, #7f0000 100%)' },
              }}
            >
              {loading
                ? <CircularProgress size={24} sx={{ color: 'white' }} />
                : tab === 0 ? 'Sign In' : 'Create Account'}
            </Button>
          </Box>
        </Paper>

        <Typography variant="body2" align="center" sx={{ mt: 3, color: 'white', opacity: 0.9 }}>
          2026 MAS Holdings. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
}
